import { useEffect } from 'react';
import { FileText, Users, Target, TrendingUp, Phone } from 'lucide-react';

const BusinessPlan = () => {
  useEffect(() => {
    document.title = 'Business Plan | He Whakaputanga nga Hapū o te Tairawhiti';
    return () => {
      document.title = 'He Whakaputanga nga Hapū o te Tairawhiti | Customary Rights Platform';
    };
  }, []);

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header Section */}
      <section className="bg-gradient-to-r from-purple-600 to-purple-700 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Hapū Business Plan Template
          </h1>
          <p className="text-xl md:text-2xl max-w-4xl mx-auto opacity-90">
            Strategic planning framework for hapū and whānau business development
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="card bg-purple-50 mb-12">
              <div className="flex items-start">
                <FileText className="h-8 w-8 text-purple-600 mr-4 mt-1" />
                <div>
                  <h2 className="text-2xl font-bold mb-4 text-gray-800">About This Template</h2>
                  <p className="text-lg text-gray-700 leading-relaxed mb-4">
                    This comprehensive business plan template is designed specifically for hapū and whānau 
                    seeking to establish or expand their business ventures while maintaining cultural values 
                    and supporting community development.
                  </p>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    The template incorporates traditional Māori business principles with modern strategic 
                    planning approaches, ensuring your venture honors whakapapa while achieving commercial success.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Table of Contents */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="section-header text-center mb-12">Business Plan Structure</h2>
          
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="card">
                <div className="flex items-center mb-3">
                  <span className="bg-purple-100 text-purple-600 rounded-full w-8 h-8 flex items-center justify-center font-semibold mr-3">1</span>
                  <h3 className="text-lg font-semibold text-gray-800">Executive Summary</h3>
                </div>
                <p className="text-gray-600 text-sm">
                  One-page overview of your entire business plan, written last but presented first
                </p>
              </div>

              <div className="card">
                <div className="flex items-center mb-3">
                  <span className="bg-purple-100 text-purple-600 rounded-full w-8 h-8 flex items-center justify-center font-semibold mr-3">2</span>
                  <h3 className="text-lg font-semibold text-gray-800">Business Details</h3>
                </div>
                <p className="text-gray-600 text-sm">
                  Legal structure, registration details, contact information, and ownership
                </p>
              </div>

              <div className="card">
                <div className="flex items-center mb-3">
                  <span className="bg-purple-100 text-purple-600 rounded-full w-8 h-8 flex items-center justify-center font-semibold mr-3">3</span>
                  <h3 className="text-lg font-semibold text-gray-800">Operations</h3>
                </div>
                <p className="text-gray-600 text-sm">
                  What you do, how you do it, and what makes your approach unique
                </p>
              </div>

              <div className="card">
                <div className="flex items-center mb-3">
                  <span className="bg-purple-100 text-purple-600 rounded-full w-8 h-8 flex items-center justify-center font-semibold mr-3">4</span>
                  <h3 className="text-lg font-semibold text-gray-800">Background</h3>
                </div>
                <p className="text-gray-600 text-sm">
                  Your story, experience, and the journey that led to this business
                </p>
              </div>

              <div className="card">
                <div className="flex items-center mb-3">
                  <span className="bg-purple-100 text-purple-600 rounded-full w-8 h-8 flex items-center justify-center font-semibold mr-3">5</span>
                  <h3 className="text-lg font-semibold text-gray-800">Mission & Strategy</h3>
                </div>
                <p className="text-gray-600 text-sm">
                  Your goals, mission statement, and strategic approach to achieving them
                </p>
              </div>

              <div className="card">
                <div className="flex items-center mb-3">
                  <span className="bg-purple-100 text-purple-600 rounded-full w-8 h-8 flex items-center justify-center font-semibold mr-3">6</span>
                  <h3 className="text-lg font-semibold text-gray-800">Team Structure</h3>
                </div>
                <p className="text-gray-600 text-sm">
                  Current and planned team, roles, responsibilities, and development plans
                </p>
              </div>

              <div className="card">
                <div className="flex items-center mb-3">
                  <span className="bg-purple-100 text-purple-600 rounded-full w-8 h-8 flex items-center justify-center font-semibold mr-3">7</span>
                  <h3 className="text-lg font-semibold text-gray-800">Market Analysis</h3>
                </div>
                <p className="text-gray-600 text-sm">
                  Market research, target customers, and industry trends
                </p>
              </div>

              <div className="card">
                <div className="flex items-center mb-3">
                  <span className="bg-purple-100 text-purple-600 rounded-full w-8 h-8 flex items-center justify-center font-semibold mr-3">8</span>
                  <h3 className="text-lg font-semibold text-gray-800">Competition</h3>
                </div>
                <p className="text-gray-600 text-sm">
                  Competitor analysis and your competitive advantages
                </p>
              </div>

              <div className="card">
                <div className="flex items-center mb-3">
                  <span className="bg-purple-100 text-purple-600 rounded-full w-8 h-8 flex items-center justify-center font-semibold mr-3">9</span>
                  <h3 className="text-lg font-semibold text-gray-800">SWOT Analysis</h3>
                </div>
                <p className="text-gray-600 text-sm">
                  Strengths, Weaknesses, Opportunities, and Threats assessment
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Business Details Template */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="section-header text-center mb-12">Business Information Template</h2>
          
          <div className="max-w-4xl mx-auto">
            <div className="card bg-purple-50 mb-8">
              <h3 className="text-xl font-semibold mb-6 text-purple-700">Core Business Details</h3>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Business Name</label>
                    <div className="border border-gray-300 rounded-md p-3 bg-white text-gray-500">
                      [Your hapū/whānau business name]
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Trading Name</label>
                    <div className="border border-gray-300 rounded-md p-3 bg-white text-gray-500">
                      [If different from business name]
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Established Date</label>
                    <div className="border border-gray-300 rounded-md p-3 bg-white text-gray-500">
                      [Date business was established]
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Business Structure</label>
                    <div className="border border-gray-300 rounded-md p-3 bg-white text-gray-500">
                      [Sole trader/Partnership/Company/Trust]
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Registration Date</label>
                    <div className="border border-gray-300 rounded-md p-3 bg-white text-gray-500">
                      [Date registered with authorities]
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">NZBN</label>
                    <div className="border border-gray-300 rounded-md p-3 bg-white text-gray-500">
                      [New Zealand Business Number]
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">IRD Number</label>
                    <div className="border border-gray-300 rounded-md p-3 bg-white text-gray-500">
                      [Tax registration number]
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">GST Registration</label>
                    <div className="border border-gray-300 rounded-md p-3 bg-white text-gray-500">
                      [Yes/No and GST number if applicable]
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="card mb-8">
              <h3 className="text-xl font-semibold mb-6 text-gray-800 flex items-center">
                <Phone className="h-6 w-6 text-purple-600 mr-2" />
                Contact Information
              </h3>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Primary Contact Name</label>
                    <div className="border border-gray-300 rounded-md p-3 bg-white text-gray-500">
                      [Main contact person]
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                    <div className="border border-gray-300 rounded-md p-3 bg-white text-gray-500">
                      [Business phone number]
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                    <div className="border border-gray-300 rounded-md p-3 bg-white text-gray-500">
                      [Business email address]
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                    <div className="border border-gray-300 rounded-md p-3 bg-white text-gray-500">
                      [Website URL if applicable]
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Social Media</label>
                    <div className="border border-gray-300 rounded-md p-3 bg-white text-gray-500">
                      [Facebook, Instagram, LinkedIn etc.]
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Physical Address</label>
                    <div className="border border-gray-300 rounded-md p-3 bg-white text-gray-500">
                      [Business location address]
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Structure */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="section-header text-center mb-12">Team & Leadership Structure</h2>
          
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8">
              <div className="card">
                <Users className="h-8 w-8 text-purple-600 mb-4" />
                <h3 className="text-xl font-semibold mb-3 text-gray-800">Current Team</h3>
                <div className="space-y-3">
                  <div className="border-l-4 border-purple-200 pl-3">
                    <p className="font-medium text-gray-800">[Name]</p>
                    <p className="text-sm text-gray-600">[Role/Position]</p>
                    <p className="text-sm text-gray-500">[Key responsibilities]</p>
                  </div>
                  <div className="border-l-4 border-purple-200 pl-3">
                    <p className="font-medium text-gray-800">[Name]</p>
                    <p className="text-sm text-gray-600">[Role/Position]</p>
                    <p className="text-sm text-gray-500">[Key responsibilities]</p>
                  </div>
                </div>
              </div>

              <div className="card">
                <Target className="h-8 w-8 text-purple-600 mb-4" />
                <h3 className="text-xl font-semibold mb-3 text-gray-800">Advisors & Mentors</h3>
                <div className="space-y-3">
                  <div className="border-l-4 border-green-200 pl-3">
                    <p className="font-medium text-gray-800">[Advisor Name]</p>
                    <p className="text-sm text-gray-600">[Area of expertise]</p>
                    <p className="text-sm text-gray-500">[How they support business]</p>
                  </div>
                  <div className="border-l-4 border-green-200 pl-3">
                    <p className="font-medium text-gray-800">[Mentor Name]</p>
                    <p className="text-sm text-gray-600">[Area of expertise]</p>
                    <p className="text-sm text-gray-500">[How they support business]</p>
                  </div>
                </div>
              </div>

              <div className="card">
                <TrendingUp className="h-8 w-8 text-purple-600 mb-4" />
                <h3 className="text-xl font-semibold mb-3 text-gray-800">Planned Growth</h3>
                <div className="space-y-3">
                  <div className="border-l-4 border-blue-200 pl-3">
                    <p className="font-medium text-gray-800">[Future Role]</p>
                    <p className="text-sm text-gray-600">[When needed]</p>
                    <p className="text-sm text-gray-500">[Skills/experience required]</p>
                  </div>
                  <div className="border-l-4 border-blue-200 pl-3">
                    <p className="font-medium text-gray-800">[Future Role]</p>
                    <p className="text-sm text-gray-600">[When needed]</p>
                    <p className="text-sm text-gray-500">[Skills/experience required]</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Executive Summary Guide */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="section-header text-center mb-12">Executive Summary Guidelines</h2>
          
          <div className="max-w-4xl mx-auto">
            <div className="card bg-purple-50">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-purple-700">Writing Your Executive Summary</h3>
                <img 
                  src="/assets/docs/image001.jpg" 
                  alt="Business.govt.nz Business Planning resources"
                  className="h-12 opacity-70"
                />
              </div>
              <p className="text-gray-700 leading-relaxed mb-6">
                The executive summary is a critical component that should be written last but presented first. 
                It's often the only section investors or partners will read initially, so it must be compelling 
                and comprehensive while remaining concise.
              </p>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-800 mb-3">What to Include:</h4>
                  <ul className="list-disc list-inside text-gray-600 space-y-1">
                    <li>Business concept and unique value proposition</li>
                    <li>Target market and customer base</li>
                    <li>Competitive advantages</li>
                    <li>Financial highlights and projections</li>
                    <li>Funding requirements (if applicable)</li>
                    <li>Team qualifications and experience</li>
                    <li>Growth strategy and milestones</li>
                  </ul>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-800 mb-3">Best Practices:</h4>
                  <ul className="list-disc list-inside text-gray-600 space-y-1">
                    <li>Keep it to one page maximum</li>
                    <li>Write in clear, concise language</li>
                    <li>Focus on benefits, not just features</li>
                    <li>Include specific numbers and metrics</li>
                    <li>Make it compelling and engaging</li>
                    <li>Tailor it to your audience</li>
                    <li>Write it last, after completing other sections</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cultural Considerations */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="section-header text-center mb-12">Cultural Integration</h2>
          
          <div className="max-w-4xl mx-auto">
            <div className="card bg-ocean-blue bg-opacity-10">
              <h3 className="text-xl font-semibold mb-4 text-ocean-deep">Māori Business Values</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                When developing your business plan, consider how traditional Māori values can guide 
                your approach and strengthen your business foundation:
              </p>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-800 mb-3">Core Values to Consider:</h4>
                  <ul className="list-disc list-inside text-gray-600 space-y-2">
                    <li><strong>Whakapapa:</strong> Relationships and connections</li>
                    <li><strong>Manaakitanga:</strong> Hospitality and care for others</li>
                    <li><strong>Kaitiakitanga:</strong> Guardianship and sustainability</li>
                    <li><strong>Whakatōhea:</strong> Unity and collective responsibility</li>
                    <li><strong>Mauri:</strong> Life force and vitality in all activities</li>
                  </ul>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-800 mb-3">Practical Applications:</h4>
                  <ul className="list-disc list-inside text-gray-600 space-y-2">
                    <li>Community benefit alongside profit</li>
                    <li>Environmental stewardship in operations</li>
                    <li>Inclusive decision-making processes</li>
                    <li>Supporting hapū and whānau development</li>
                    <li>Cultural protocols in business practices</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BusinessPlan;